import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OnaResponse, OmissionsResponse, FundamentalsResponse, HeatmapResponse } from '@/api/types';
import { buildOuTree, type OuNode } from '@/components/common/OuTree/ouTree';
import { useOUItems } from '@/hooks/useOUs';
import { useUsers } from '@/hooks/useUsers';
import { fmtScore } from '@/utils/analytics';
import { OrgTreeNode } from '../components/OrgTreeNode';
import type { OrgNode } from '@/types/analytics.types';

interface OnaTabProps {
  apiOna?: OnaResponse;
  apiOmissions?: OmissionsResponse;
  /** Usadas únicamente para completar el KPI "Score promedio" (ver `resolveAvgScore`) —
   *  mismas instancias ya calculadas en AnalyticsView/index.tsx para los otros tabs. */
  apiFundamentals?: FundamentalsResponse;
  apiHeatmap?: HeatmapResponse;
}

/** Score promedio real para el KPI de esta vista: preferimos el promedio ponderado por
 *  fundamentales (mismo criterio que ResumenTab/FundamentalsTab); si no hay fundamentales con
 *  datos, se calcula desde las celdas numéricas de apiHeatmap. Sin ambas fuentes, no hay dato. */
function resolveAvgScore(
  fundamentals: FundamentalsResponse | undefined,
  heatmap: HeatmapResponse | undefined,
): number | undefined {
  const totalEvaluations = fundamentals?.reduce((sum, f) => sum + f.evaluations, 0) ?? 0;
  if (fundamentals && totalEvaluations > 0) {
    const weightedSum = fundamentals.reduce((sum, f) => sum + f.score * f.evaluations, 0);
    return weightedSum / totalEvaluations;
  }
  const heatmapScores = (heatmap?.rows ?? []).flatMap((row) =>
    row.scores.filter((score): score is number => typeof score === 'number'),
  );
  if (heatmapScores.length > 0) {
    return heatmapScores.reduce((sum, score) => sum + score, 0) / heatmapScores.length;
  }
  return undefined;
}

const PAGE_SIZE = 5;

/** Umbrales heurísticos sobre `responseCount` real (no hay un porcentaje de omisión en el
 *  backend): pocas respuestas recibidas = más riesgo de omisión. */
function omissionColor(responseCount: number): string {
  if (responseCount === 0) return '#ef4444';
  if (responseCount <= 2) return '#f59e0b';
  return '#10b981';
}

const MATURITY_LEGEND = [
  { range: '≤ 2.0', bg: '#fef2f2', text: '#991b1b', ring: '#fca5a5', labelKey: 'analytics.heatmap.legendCritical' },
  { range: '2.0–2.5', bg: '#fffbeb', text: '#92400e', ring: '#fcd34d', labelKey: 'analytics.heatmap.legendLow' },
  { range: '2.5–3.0', bg: '#fefce8', text: '#713f12', ring: '#fde047', labelKey: 'analytics.heatmap.legendMedium' },
  { range: '3.0–3.5', bg: '#ecfdf5', text: '#065f46', ring: '#6ee7b7', labelKey: 'analytics.heatmap.legendGood' },
  { range: '≥ 3.5',  bg: '#d1fae5', text: '#064e3b', ring: '#34d399', labelKey: 'analytics.heatmap.legendExcellent' },
];

/** Recolecta recursivamente el id del nodo y de todos sus descendientes reales
 *  (misma lógica que HeatmapTab.tsx — duplicada localmente porque esa vista no se toca en esta tarea). */
function collectDescendantIds(node: OuNode, into: Set<string>): void {
  into.add(node.ou.id);
  node.children.forEach((child) => collectDescendantIds(child, into));
}

export function OnaTab({ apiOna, apiOmissions, apiFundamentals, apiHeatmap }: OnaTabProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const { data: ouItems = [] } = useOUItems({ activeOnly: false });
  // Fetch único de todo el roster del tenant (no un fetch por nodo del árbol) — se usa solo
  // para resolver ouId/displayName por evaluatedId, dato que ni apiOna ni apiOmissions traen.
  const { data: users = [] } = useUsers();

  // Árbol completo de OUs construido una sola vez a partir del catálogo plano.
  const ouForest = useMemo<OuNode[]>(() => buildOuTree(ouItems), [ouItems]);

  const ouIdByUserId = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((user) => {
      if (user.ouId && user.ouId !== 'todas') map.set(user.id, user.ouId);
    });
    return map;
  }, [users]);

  const nameByUserId = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((user) => map.set(user.id, user.name));
    return map;
  }, [users]);

  // Interacciones recibidas por OU: se agrupan por el `evaluatedId` de cada arista (quien
  // recibe la evaluación/interacción), no por `evaluatorId` (quien la emite) — esta vista mide
  // actividad recibida por unidad, análogo a lo que ya hace HeatmapTab con sus filas.
  // Las aristas cuyo evaluatedId no resuelve a una OU conocida (usuario fuera del roster
  // cargado por useUsers) se excluyen del agregado en vez de mostrarse en una OU incorrecta.
  const { interactionsByOu, evaluatedIdsByOu } = useMemo(() => {
    const interactions = new Map<string, number>();
    const evaluatedIds = new Map<string, Set<string>>();
    for (const edge of apiOna?.edges ?? []) {
      const ouId = ouIdByUserId.get(edge.evaluatedId);
      if (!ouId) continue;
      interactions.set(ouId, (interactions.get(ouId) ?? 0) + edge.interactionCount);
      const set = evaluatedIds.get(ouId) ?? new Set<string>();
      set.add(edge.evaluatedId);
      evaluatedIds.set(ouId, set);
    }
    return { interactionsByOu: interactions, evaluatedIdsByOu: evaluatedIds };
  }, [apiOna, ouIdByUserId]);

  // Mapea el árbol real de OUs a `OrgNode` (mismo shape que consume OrgTreeNode). No hay
  // niveles BU/Dept/Person distintos en el modelo real: todo nodo es 'OU'. `ev`/`p` son la suma
  // recursiva de interacciones/personas evaluadas de la OU y todas sus descendientes reales.
  const rootNodes = useMemo<OrgNode[]>(() => {
    function toOrgNode(node: OuNode): OrgNode {
      const descendantIds = new Set<string>();
      collectDescendantIds(node, descendantIds);
      let interactions = 0;
      const evaluatedPeople = new Set<string>();
      descendantIds.forEach((ouId) => {
        interactions += interactionsByOu.get(ouId) ?? 0;
        evaluatedIdsByOu.get(ouId)?.forEach((personId) => evaluatedPeople.add(personId));
      });
      return {
        id: node.ou.id,
        n: node.ou.name,
        t: 'OU',
        ev: interactions,
        p: evaluatedPeople.size,
        children: node.children.map(toOrgNode),
      };
    }
    return ouForest.map(toOrgNode);
  }, [ouForest, interactionsByOu, evaluatedIdsByOu]);

  const totalPages = Math.ceil(rootNodes.length / PAGE_SIZE);
  const paged = rootNodes.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Ranking de omisiones: fuente real (apiOmissions.participants), ordenado ascendente por
  // responseCount (menos respuestas recibidas = mayor riesgo de omisión). Sin fallback al
  // árbol mock: mientras apiOmissions no ha cargado (undefined), se muestra un estado de
  // carga explícito.
  const omRanking = useMemo(() => {
    if (!apiOmissions) return null;
    return apiOmissions.participants
      .slice()
      .sort((a, b) => a.responseCount - b.responseCount)
      .slice(0, 10)
      .map((participant) => ({
        id: participant.evaluatedId,
        name: nameByUserId.get(participant.evaluatedId) ?? participant.evaluatedId,
        value: participant.responseCount,
      }));
  }, [apiOmissions, nameByUserId]);

  const totalInteractions = (apiOna?.edges ?? []).reduce((sum, edge) => sum + edge.interactionCount, 0);
  const networkPeopleIds = new Set<string>();
  (apiOna?.edges ?? []).forEach((edge) => {
    networkPeopleIds.add(edge.evaluatorId);
    networkPeopleIds.add(edge.evaluatedId);
  });

  const highestOm = omRanking && omRanking.length > 0 ? omRanking[0]!.value : null;

  const avgScore = resolveAvgScore(apiFundamentals, apiHeatmap);

  const kpis = [
    { label: t('analytics.ona.avgScore'),        value: fmtScore(avgScore),            color: '#059669' },
    { label: t('analytics.ona.totalEvals'),      value: totalInteractions.toLocaleString(), color: '#2563eb' },
    { label: t('analytics.ona.collaboratorsKpi'), value: networkPeopleIds.size,         color: '#F08521' },
    {
      label: t('analytics.ona.highestOmission'),
      value: highestOm !== null ? `${highestOm} ${t('analytics.ona.evalsShort')}` : '—',
      color: '#ef4444',
    },
  ];

  return (
    <>
      <div className="au-card">
        {/* Card header */}
        <div className="au-card-header">
          <div>
            <div className="au-card-title">
              <i className="fas fa-project-diagram" aria-hidden="true" />
              {t('analytics.ona.title')}
            </div>
            <div className="au-card-sub">{t('analytics.ona.subtitle')}</div>
          </div>
        </div>

        {/* KPIs — label on top, colored value */}
        <div className="ona2-kpis">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="ona2-kpi">
              <div className="ona2-kpi-label">{kpi.label}</div>
              <div className="ona2-kpi-value" style={{ color: kpi.color }}>{kpi.value}</div>
            </div>
          ))}
        </div>

        <div className="ona2-layout">
          {/* Tree */}
          <div>
            <div className="ona2-tree-hint">
              {t('analytics.ona.treeHint')}
            </div>
            <div className="ona2-tree">
              {paged.map((node) => (
                <OrgTreeNode key={node.id} node={node} depth={0} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="ona2-pager">
                <span className="ona2-pager-info">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, rootNodes.length)} / {rootNodes.length}
                </span>
                <div className="ona2-pager-btns">
                  <button
                    className="ona2-pager-btn"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                    aria-label={t('common.back')}
                  >
                    <i className="fas fa-chevron-left" aria-hidden="true" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      className={`ona2-pager-btn${i === page ? ' active' : ''}`}
                      onClick={() => setPage(i)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="ona2-pager-btn"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label={t('common.next')}
                  >
                    <i className="fas fa-chevron-right" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="ona2-sidebar">
            {/* Omissions ranking */}
            <h3 className="ona2-sidebar-title">{t('analytics.ona.omissionsRanking')}</h3>
            <div className="ona2-ranking">
              {omRanking === null ? (
                <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
                  {t('common.loading')}
                </span>
              ) : omRanking.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
                  {t('analytics.noData')}
                </span>
              ) : (
                omRanking.map((item, idx) => {
                  const c = omissionColor(item.value);
                  return (
                    <div key={item.id} className="ona2-rank-item">
                      <div className="ona2-rank-top">
                        <div className="ona2-rank-left">
                          <span className="ona2-rank-num">{idx + 1}</span>
                          <span className="ona2-rank-name">{item.name}</span>
                        </div>
                        <span className="ona2-rank-evals">
                          {item.value} {t('analytics.ona.evalsShort')}
                        </span>
                      </div>
                      <div className="ona2-ombar">
                        <div className="ona2-ombar-track">
                          <div
                            className="ona2-ombar-fill"
                            style={{ width: `${Math.min(item.value * 10, 100)}%`, background: c }}
                          />
                        </div>
                        <span style={{ color: c, fontSize: 11, fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
                          {item.value}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Maturity legend */}
            <div className="ona2-legend">
              <div className="ona2-legend-title">{t('analytics.ona.maturityCulture')}</div>
              {MATURITY_LEGEND.map((lv) => (
                <div key={lv.range} className="ona2-legend-item">
                  <span
                    className="ona2-legend-dot"
                    style={{ background: lv.bg, borderColor: lv.ring, color: lv.text }}
                  />
                  <span className="ona2-legend-label">{t(lv.labelKey)}</span>
                  <span className="ona2-legend-range">{lv.range}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
